/**
 * F23 (Wave 2C) — admin commission API client.
 *
 * Endpoints:
 *   GET    /admin/commission-rates                        → listRates (paginated)
 *   GET    /admin/commission-rates/:venueId               → rateDetail (with history)
 *   PUT    /admin/commission-rates/:venueId               → setVenueRate
 *   GET    /admin/commission-statements                   → listStatements (paginated, filters)
 *   GET    /admin/commission-statements/:id               → statementDetail (with records)
 *   POST   /admin/commission-statements/:id/settle        → markStatementSettled
 *   POST   /admin/commission-statements/:id/void          → voidStatement
 *   POST   /admin/commission-statements/:id/issue         → issueStatement (manual)
 *
 * Permission gating happens server-side. The page-level <ProtectedRoute>
 * gates which routes a given admin role can navigate to; the API client
 * forwards requests and relies on the server to enforce RBAC.
 */
import type {
  CommissionSource,
  CommissionStatement,
  CommissionStatementDetail,
  CommissionStatementStatus,
  MarkStatementSettledInput,
  SetVenueCommissionRateInput,
  VenueCommissionRate,
  VoidStatementInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// ─── Rate list / detail response envelopes ──────────────────────────────────

export interface AdminCommissionRateRow {
  readonly venueId: string;
  readonly venueName: string;
  readonly venueSlug: string;
  readonly activeRate: {
    readonly id: string | null;
    /**
     * CST-011 — the backend's `activeRateBp` is NULLABLE (LEFT JOIN LATERAL):
     * a venue with zero rate rows yields null, which the list page renders as
     * an explicit '—' (NOT a coerced '0.00%').
     */
    readonly rateBasisPoints: number | null;
    readonly effectiveAt: string;
    readonly setByAdminDisplayName: string | null;
    readonly reason: string | null;
  };
  readonly lastChangedAt: string | null;
}

export interface AdminCommissionRatesListResponse {
  readonly data: readonly AdminCommissionRateRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/**
 * CST-003 — matches the backend's REAL `GET /admin/commission-rates/:venueId`
 * payload: `{ data: { activeRate: { rateBasisPoints, effectiveAt, source },
 * history } }`. There is NO `venue` key and the active rate carries no
 * id/admin/reason fields (the history rows do). `source` tells whether the
 * rate comes from a `venue_commission_rates` row (`db`) or the platform
 * default fallback (`fallback_default`).
 */
export interface AdminCommissionRateDetailResponse {
  readonly activeRate: {
    readonly rateBasisPoints: number;
    readonly effectiveAt: string;
    readonly source: 'db' | 'fallback_default';
  };
  readonly history: readonly VenueCommissionRate[];
}

// ─── Statement list / detail response envelopes ─────────────────────────────

export interface AdminCommissionStatementRow extends CommissionStatement {
  readonly venueName: string;
  readonly venueSlug: string;
  /**
   * F35 — trainer-source rows carry a trainer display name (resolved by the
   * server-side name lookup, 2CD). Optional + nullable so venue rows + pre-2CD
   * servers (which return only `venueName`/`venueSlug`) stay valid.
   */
  readonly trainerDisplayName?: string | null;
}

export interface AdminCommissionStatementsListResponse {
  readonly data: readonly AdminCommissionStatementRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

export type AdminCommissionStatementDetailResponse = CommissionStatementDetail & {
  readonly venueName: string;
  readonly venueSlug: string;
  /**
   * CST-012 — for trainer-source statements the server resolves the trainer's
   * display name (venueName is the '—' placeholder for those rows); the detail
   * page uses it for the title + the Void type-to-confirm label.
   */
  readonly trainerDisplayName?: string | null;
};

// ─── Query option types ─────────────────────────────────────────────────────

export interface ListRatesOpts {
  readonly q?: string;
  readonly page?: number;
  readonly perPage?: number;
}

export interface ListStatementsOpts {
  /**
   * CST-011 — SINGLE status value only (`'all'` / omitted = no filter). The
   * backend's `.strict()` schema validates a single `z.enum` value; arrays /
   * CSV 422 (`trainerBilling.ts` already documents getting burned by this).
   */
  readonly status?: CommissionStatementStatus | 'all';
  /**
   * F35 — filter by statement source. SINGLE value only (same backend
   * constraint as `status`). `'all'` / omitted = no filter.
   */
  readonly source?: CommissionSource | 'all';
  /** F35 — restrict to one trainer's invoices (used by the per-trainer billing page). */
  readonly trainerUserId?: string;
  readonly venueId?: string;
  /** ISO 8601 timestamp (inclusive lower bound of period_start). */
  readonly periodStart?: string;
  /** ISO 8601 timestamp (exclusive upper bound of period_start). */
  readonly periodEnd?: string;
  readonly page?: number;
  readonly perPage?: number;
}

/**
 * CST-005 — the backend's detail query params are `recordsPage` /
 * `recordsPerPage` (a `.strict()` schema — the previous `page`/`perPage`
 * names 422'd). They paginate the statement's attached records server-side;
 * the response's `recordsPagination` sidecar carries the real total.
 */
export interface StatementDetailOpts {
  readonly recordsPage?: number;
  readonly recordsPerPage?: number;
}

interface DataEnvelope<T> {
  readonly data: T;
}

function buildRatesQuery(opts: ListRatesOpts): string {
  return buildQuery({
    q: opts.q,
    page: opts.page,
    perPage: opts.perPage,
  });
}

function buildStatementsQuery(opts: ListStatementsOpts): string {
  const params: Record<string, string | number | boolean | undefined | null> = {
    venueId: opts.venueId,
    trainerUserId: opts.trainerUserId,
    periodStart: opts.periodStart,
    periodEnd: opts.periodEnd,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') params.status = opts.status;
  if (opts.source && opts.source !== 'all') params.source = opts.source;
  return buildQuery(params);
}

function buildDetailQuery(opts: StatementDetailOpts): string {
  return buildQuery({
    recordsPage: opts.recordsPage,
    recordsPerPage: opts.recordsPerPage,
  });
}

// ─── API surface ────────────────────────────────────────────────────────────

export const adminCommissionApi = {
  /** GET /admin/commission-rates — paginated venue rates table. */
  listRates: async (opts?: ListRatesOpts): Promise<AdminCommissionRatesListResponse> => {
    // The API returns flat rows; the client + table expect a nested
    // `activeRate` object. Map between them here.
    interface RawRow {
      readonly venueId: string;
      readonly venueName: string;
      readonly venueSlug: string;
      /** CST-011 — nullable on the wire (LEFT JOIN LATERAL; zero rate rows → null). */
      readonly activeRateBp: number | null;
      readonly activeEffectiveAt: string | null;
      readonly lastChangedAt: string | null;
      readonly lastChangedByAdminDisplayName: string | null;
    }
    const raw = await adminFetch<{
      data: readonly RawRow[];
      pagination: { page: number; perPage: number; total: number };
    }>(`/admin/commission-rates${buildRatesQuery(opts ?? {})}`);
    return {
      data: raw.data.map((r) => ({
        venueId: r.venueId,
        venueName: r.venueName,
        venueSlug: r.venueSlug,
        activeRate: {
          id: null,
          rateBasisPoints: r.activeRateBp,
          effectiveAt: r.activeEffectiveAt ?? new Date(0).toISOString(),
          setByAdminDisplayName: r.lastChangedByAdminDisplayName,
          reason: null,
        },
        lastChangedAt: r.lastChangedAt,
      })),
      pagination: raw.pagination,
    };
  },

  /** GET /admin/commission-rates/:venueId — active rate + history. */
  rateDetail: async (venueId: string): Promise<AdminCommissionRateDetailResponse | null> => {
    try {
      // CST-003 — the backend wraps the payload in a `{ data }` envelope.
      const body = await adminFetch<DataEnvelope<AdminCommissionRateDetailResponse>>(
        `/admin/commission-rates/${encodeURIComponent(venueId)}`,
      );
      return body.data;
    } catch (err) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'status' in err &&
        (err as { status: number }).status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * PUT /admin/commission-rates/:venueId — change a venue's commission rate.
   *
   * Permission: `commission_rate:write`. Rate-limit 30/h (server-enforced).
   * Forward-only semantics: writes a new `venue_commission_rates` row; never
   * UPDATEs an existing one. CST-011 — the backend returns the NEW
   * `venue_commission_rates` row (`{ data: VenueCommissionRate }`), not the
   * rate-detail shape.
   */
  setVenueRate: async (
    venueId: string,
    input: SetVenueCommissionRateInput,
  ): Promise<VenueCommissionRate> => {
    const body = await adminFetch<DataEnvelope<VenueCommissionRate>>(
      `/admin/commission-rates/${encodeURIComponent(venueId)}`,
      {
        method: 'PUT',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /** GET /admin/commission-statements — paginated queue with filters. */
  listStatements: async (
    opts?: ListStatementsOpts,
  ): Promise<AdminCommissionStatementsListResponse> => {
    return adminFetch<AdminCommissionStatementsListResponse>(
      `/admin/commission-statements${buildStatementsQuery(opts ?? {})}`,
    );
  },

  /** GET /admin/commission-statements/:id — full statement detail. */
  statementDetail: async (
    id: string,
    opts?: StatementDetailOpts,
  ): Promise<AdminCommissionStatementDetailResponse | null> => {
    try {
      const body = await adminFetch<DataEnvelope<AdminCommissionStatementDetailResponse>>(
        `/admin/commission-statements/${encodeURIComponent(id)}${buildDetailQuery(opts ?? {})}`,
      );
      return body.data;
    } catch (err) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'status' in err &&
        (err as { status: number }).status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * POST /admin/commission-statements/:id/settle — admin marks settled.
   *
   * FIX 6.1 — settle / void / issue reply `{ data: enrichStatement(…) }`: the
   * BARE statement + `venueName`/`venueSlug`/`trainerDisplayName`, with NO
   * `records` / `recordsPagination` (those come only from `statementDetail`).
   * Callers refetch the detail after a mutation.
   */
  markStatementSettled: async (
    id: string,
    input: MarkStatementSettledInput,
  ): Promise<AdminCommissionStatementRow> => {
    const body = await adminFetch<DataEnvelope<AdminCommissionStatementRow>>(
      `/admin/commission-statements/${encodeURIComponent(id)}/settle`,
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /** POST /admin/commission-statements/:id/void — admin voids the statement. */
  voidStatement: async (
    id: string,
    input: VoidStatementInput,
  ): Promise<AdminCommissionStatementRow> => {
    const body = await adminFetch<DataEnvelope<AdminCommissionStatementRow>>(
      `/admin/commission-statements/${encodeURIComponent(id)}/void`,
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /** POST /admin/commission-statements/:id/issue — admin manual issue (rare). */
  issueStatement: async (id: string): Promise<AdminCommissionStatementRow> => {
    const body = await adminFetch<DataEnvelope<AdminCommissionStatementRow>>(
      `/admin/commission-statements/${encodeURIComponent(id)}/issue`,
      {
        method: 'POST',
        body: {} as Record<string, unknown>,
      },
    );
    return body.data;
  },
};
